import { Dialog } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import { X } from "heroicons-react"
import { User, TakedownBatch, UserTakedown, TakedownTrack, SongSelection } from "../../../../../types"
import { KButton, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { formatNumber, getCurrency, mapToCamelCase } from "../../../../../utility"
import Loading from "../../../../ui/Loading"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import SongSelectionOwnershipUpdateModal from "./SongSelectionOwnershipUpdateModal"

const albumColumnWidths = {
  TOGGLE: "4%",
  ALBUM_TITLE: "30%",
  ALBUM_UPC: "14%",
  ALBUM_DISTRIBUTOR: "18%",
  TRACK_COUNT: "10%",
  OFFER_LTM: "12%",
  ADVANCE_OFFER: "12%"
}

const trackColumnWidths = {
  TRACK_TITLE: "40%",
  TRACK_ISRC: "20%",
  SPLIT_RATE: "12%",
  OFFER_LTM: "14%",
  ADVANCE_OFFER: "14%"
}

type TakedownAlbumGroup = {
  key: string
  album: TakedownTrack["album"]
  tracks: TakedownTrack[]
  ltmIncomeTotal: number
  advanceOfferTotal: number
}

const albumColumnLabels: Record<keyof typeof albumColumnWidths, string> = {
  TOGGLE: "",
  ALBUM_TITLE: "Album",
  ALBUM_UPC: "UPC",
  ALBUM_DISTRIBUTOR: "Distributor",
  TRACK_COUNT: "Tracks",
  OFFER_LTM: "LTM",
  ADVANCE_OFFER: "Total Offer"
}

const trackColumnLabels: Record<keyof typeof trackColumnWidths, string> = {
  TRACK_TITLE: "Track",
  TRACK_ISRC: "ISRC",
  SPLIT_RATE: "Split Rate",
  OFFER_LTM: "LTM",
  ADVANCE_OFFER: "Offer"
}

const getAdvanceOffer = (track: TakedownTrack) => track.songSelection?.advanceOffer || 0

const getLtmIncome = (track: TakedownTrack) => track.songSelection?.ltmIncome || 0

const sortTracks = (trackList: TakedownTrack[]) =>
  [...trackList].sort((a, b) => {
    const offerDifference = getAdvanceOffer(b) - getAdvanceOffer(a)
    if (offerDifference !== 0) {
      return offerDifference
    }

    const titleComparison = (a.title || "").localeCompare(b.title || "")
    if (titleComparison !== 0) {
      return titleComparison
    }

    return (a.isrc || "").localeCompare(b.isrc || "")
  })

const getAlbumGroupKey = (track: TakedownTrack) => {
  const album = track.album
  return album?.upc?.trim() || `${track.albumId}-${album?.title || "unknown-album"}`
}

const groupTracksByAlbum = (trackList: TakedownTrack[]): TakedownAlbumGroup[] => {
  const groupedTracks = new Map<string, TakedownAlbumGroup>()

  trackList.forEach((track) => {
    const key = getAlbumGroupKey(track)
    const existingGroup = groupedTracks.get(key)

    if (existingGroup) {
      existingGroup.tracks.push(track)
      existingGroup.ltmIncomeTotal += getLtmIncome(track)
      existingGroup.advanceOfferTotal += getAdvanceOffer(track)
      return
    }

    groupedTracks.set(key, {
      key,
      album: track.album,
      tracks: [track],
      ltmIncomeTotal: getLtmIncome(track),
      advanceOfferTotal: getAdvanceOffer(track)
    })
  })

  return Array.from(groupedTracks.values())
    .map((group) => ({
      ...group,
      tracks: sortTracks(group.tracks)
    }))
    .sort((a, b) => {
      const offerDifference = b.advanceOfferTotal - a.advanceOfferTotal
      if (offerDifference !== 0) {
        return offerDifference
      }

      return (a.album?.title || "").localeCompare(b.album?.title || "")
    })
}

const AdminTakedownContractModal = ({
  selectedContractTakedown,
  handleClose,
  user
}: {
  selectedContractTakedown?: UserTakedown
  handleClose: () => void
  user: User
}) => {
  const [takedownBatch, setTakedownBatch] = useState<TakedownBatch>()
  const [loading, setLoading] = useState(false)
  const [selectedSong, setSelectedSong] = useState<SongSelection | undefined>(undefined)
  const [expandedAlbumKey, setExpandedAlbumKey] = useState<string>()

  useEffect(() => {
    if (!selectedContractTakedown) {
      return
    }
    if (!selectedContractTakedown.confirmedOfferId) {
      toast.error("Confirmed Offer ID missing. Create +User Takedown with Confirmed Offer ID", errorFilledToastOptions)
      handleClose()
      return
    }
    setLoading(true)
    users(
      `/list_takedown_batch_list/?album_ids=${selectedContractTakedown.albumIds}&confirmed_offer_id=${selectedContractTakedown.confirmedOfferId}`,
      "GET"
    )
      .then((resp) => {
        const takedownBatch: TakedownBatch = mapToCamelCase(resp.data)
        setTakedownBatch(takedownBatch)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error fetching takedown batch", errorFilledToastOptions)
      })
  }, [selectedContractTakedown])

  useEffect(() => {
    const groupedTrackList = groupTracksByAlbum(takedownBatch?.trackList || [])
    if (groupedTrackList.length === 0) {
      setExpandedAlbumKey(undefined)
      return
    }

    setExpandedAlbumKey((currentExpandedAlbumKey) => {
      if (currentExpandedAlbumKey && groupedTrackList.some((albumGroup) => albumGroup.key === currentExpandedAlbumKey)) {
        return currentExpandedAlbumKey
      }

      return groupedTrackList[0].key
    })
  }, [takedownBatch])

  // this will check if the track has split rate related potential problems
  const checkPotentialSplitRate = (song: TakedownTrack, artistId: number | undefined) => {
    // IMPORTANT NOTE: Update confirmed offer page in Scouting too if you update here.
    const track = { ...song }
    const warnings = []
    const artistList = track?.artists?.filter((artist) => !artist.name.includes("Kahuna Artist")) || []
    const trackTitle = track?.title
    const unwantedTexts = ["feat.", ".feat", "with", "&"]
    const matchedUnwantedText = unwantedTexts.find((text: string) => trackTitle?.toLowerCase().includes(text))
    let isSingleAndMainArtist = true

    if (artistList.length > 1) {
      warnings.push(`This song has multiple artists: ${artistList.map((artist) => artist.name).join(", ")}`)
      isSingleAndMainArtist = false
    }

    if (artistList.length > 0 && artistList[0].id !== artistId) {
      warnings.push(`Offer artist is not the main artist. Main artist is: ${artistList[0]?.name}`)
      isSingleAndMainArtist = false
    }

    if (matchedUnwantedText) {
      warnings.push(`This track title includes unwanted pattern: "${matchedUnwantedText}"`)
    }

    return {warnings, isSingleAndMainArtist}
  }

  const renderBadge = (
    icon: string,
    text: string,
    fontColor: string,
    bgColor: string,
    tooltipText: string,
    index: number
  ) => {
    return (
      <KTooltip
        position={index < 4 ? "right" : "left"}
        showArrow
        padding="0px 4px"
        backgroundColor="#111"
        children={
          <div
            style={{
              backgroundColor: bgColor,
              cursor: "default"
            }}
            className={`flex flex-row items-center gap-0.5 rounded-[100px] p-1 px-2 z-10000`}
          >
            <img src={icon} />
            <KSpan text={text} color={fontColor} fontSize={12} fontWeight={500} lineHeight="14px" />
          </div>
        }
        content={
          <div className="w-[200px] h-auto whitespace-normal">
            <KSpan text={tooltipText} color="#FFF" fontSize={12} lineHeight="14px" />
          </div>
        }
      />
    )
  }

  const updateSongSelectionOwnership = async (songSelectionId: number, ownership: number) => {
    const artistId = takedownBatch?.artistId
    const userId = user?.id

    if (!artistId || !userId) return
    setLoading(true)
    users(
      `/update_takedown_song_selection_ownership/?song_selection_id=${songSelectionId}&ownership=${ownership}&artist_id=${artistId}&artist_user_id=${userId}`,
      "PUT"
    )
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Split rate has been updated successfully", successFilledToastOptions)
          if (!takedownBatch) {
            setLoading(false)
            return
          }
          const trackList = { ...takedownBatch }.trackList
          const updatedTrackList = trackList?.map((track: TakedownTrack) =>
            track.songSelection.id === songSelectionId
              ? { ...track, songSelection: { ...track.songSelection, ownership } }
              : { ...track }
          )
          setTakedownBatch({ ...takedownBatch, trackList: updatedTrackList })
        } else {
          toast.error("Error on updating split rate", errorFilledToastOptions)
        }
        setSelectedSong(undefined)
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error on updating split rate", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const handleExport = () => {
    const groupedTrackList = groupTracksByAlbum(takedownBatch?.trackList || [])
    const headers = ["ALBUM_TITLE", "ALBUM_UPC", "ALBUM_DISTRIBUTOR", "TRACK_TITLE", "TRACK_ISRC", "SPLIT_RATE", "OFFER_LTM", "ADVANCE_OFFER"]
    const rows = groupedTrackList.flatMap((albumGroup) =>
      albumGroup.tracks.map((track) => [
        albumGroup.album?.title || "",
        albumGroup.album?.upc || "",
        albumGroup.album?.distributor || "",
        track.title || "",
        track.isrc || "",
        track.songSelection?.ownership?.toString() || "",
        track.songSelection?.ltmIncome?.toString() || "",
        track.songSelection?.advanceOffer?.toString() || ""
      ])
    )
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.setAttribute("download", "takedown_batch_export.csv")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderTakedownBatches = () => {
    const takedowns = { ...takedownBatch }
    const retrievedSheetValues = takedowns?.sheetValues ?? []
    const groupedTrackList = groupTracksByAlbum(takedowns.trackList ?? [])
    const trackList = groupedTrackList.flatMap((albumGroup) => albumGroup.tracks)
    const currency = getCurrency(takedownBatch?.currency || "")
    const affiliate = takedownBatch?.affiliate
    const affiliateCommissionRate = affiliate?.affiliateDealTerms?.advanceCommissionRate 
    const affiliateComissionExist = affiliate && affiliateCommissionRate

    const advanceOfferTotal = trackList.reduce(
      (sum, track) => sum + (track.songSelection?.advanceOffer || 0),
      0
    )

    const artistId = takedowns.artistId // id of the artist of the confirmed offer (not spotify Id, it is primary key id)

    let splitRateExistInSheet = true
    const headerRow = retrievedSheetValues[0]
    if (!(headerRow?.length >= 14 && headerRow[13] === "split_rate")) {
      splitRateExistInSheet = false
    }

    const splitRateMissingRowNumber: { [key: string]: number } = {}
    const splitRateMap: { [key: string]: string[] } = {} // key:isrc, value: split rate

    if (splitRateExistInSheet) {
      trackList.forEach((track: TakedownTrack) => {
        // const songTitle = songSelection?.track?.title do not use this, because it takes the track title from Track model,
        // we need the one from the SongSelection model (It is more reliable when there is an encoding related problem in reports)
        const songTitle = track?.title?.trim() || ""
        const songIsrc = track?.isrc?.trim() || ""
        retrievedSheetValues.forEach((sheetRow) => {
          const rowLength = sheetRow?.length
          const rowSongTitle = sheetRow?.[3]?.trim() || ""
          const rowIsrc = sheetRow?.[4]?.trim() || ""
          if (
            ((rowIsrc && songIsrc && rowIsrc === songIsrc) ||
              (rowSongTitle && songTitle && rowSongTitle === songTitle)) &&
            rowLength < 14
          ) {
            // if length is smaller than 14 it means split_rate cell is empty.
            if (Object.hasOwn(splitRateMissingRowNumber, songIsrc)) {
              splitRateMissingRowNumber[songIsrc] += 1
            } else {
              splitRateMissingRowNumber[songIsrc] = 1
            }
          } else if (
            ((rowIsrc && songIsrc && rowIsrc === songIsrc) ||
              (rowSongTitle && songTitle && rowSongTitle === songTitle)) &&
            rowLength >= 14
          ) {
            const splitRate = sheetRow[13]
            if (!Object.hasOwn(splitRateMap, songIsrc)) {
              splitRateMap[songIsrc] = []
            }
            if (!splitRateMap[songIsrc].includes(splitRate)) {
              splitRateMap[songIsrc].push(splitRate)
            }
          }
        })
      })
    }

    return (
      <div className="mt-8">
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Platform Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.platformRoyalties || {})?.map(([platform, value]) => {
              return (
                <div key={`${platform}-${value}`}>
                  <KSpan text={`${platform}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Countries Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.countryRoyalties || {})?.map(([country, value]) => {
              return (
                <div key={`${country}-${value}`}>
                  <KSpan text={`${country.toUpperCase()}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Platforms Not Supported by Kahuna " fontSize={16} />
          <div className="flex gap-2 py-1 flex-row items-center flex-wrap">
            {!(
              takedownBatch?.unsupportedPlatforms && takedownBatch?.unsupportedPlatforms?.split(", ")?.length !== 0
            ) && <KSpan text="-" />}
            {takedownBatch?.unsupportedPlatforms?.split(", ").map((unsupportedPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/alert-circle.svg",
                unsupportedPlatform,
                "#EF4343",
                "#EF434333",
                "Not supported by Kahuna distribution network.",
                index
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Missing Expected Platforms in Report" fontSize={16} />
          <div className="flex gap-2 py-1 flex-row items-center flex-wrap">
            {takedownBatch?.missingPlatformsRisk?.length === 0 &&
              takedownBatch?.missingPlatformsOpportunity?.length === 0 && <KSpan text="-" />}
            {takedownBatch?.missingPlatformsRisk?.map((missingPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/alert-circle.svg",
                missingPlatform,
                "#EF4343",
                "#EF434333",
                "Expected platforms missing — report may be incomplete.",
                index
              )
            })}
            {takedownBatch?.missingPlatformsOpportunity?.map((missingPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/trend-up.svg",
                missingPlatform,
                "#16A249",
                "#16A24933",
                "Potential new revenue opportunity after migration.",
                index + takedownBatch?.missingPlatformsRisk?.length
              )
            })}
          </div>
        </div>
        <div>
          <div style={{ minWidth: "100%" }} className="flex mt-10">
            {Object.entries(albumColumnWidths).map(([header, width]) => {
              return (
                <div style={{ width }} key={`header_${header}`} className={header === "TOGGLE" ? "" : "px-1"}>
                  <KSpan
                    color="#111"
                    text={albumColumnLabels[header as keyof typeof albumColumnWidths]}
                    fontWeight={500}
                  />
                </div>
              )
            })}
          </div>
        </div>
        {groupedTrackList.map((albumGroup) => {
          const isExpanded = expandedAlbumKey === albumGroup.key

          return (
            <div
              key={`album_${albumGroup.key}`}
              className="mt-3"
              style={{ borderTop: "1px dashed gray", width: "100%" }}
            >
              <button
                type="button"
                className="w-full pt-3 pb-3 flex items-center text-left"
                onClick={() => {
                  setExpandedAlbumKey(isExpanded ? undefined : albumGroup.key)
                }}
              >
                <div style={{ width: albumColumnWidths.TOGGLE }} className="flex items-center justify-center">
                  <img
                    src={isExpanded ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                    className="w-4 h-4"
                  />
                </div>
                <div style={{ width: albumColumnWidths.ALBUM_TITLE }} className="px-1">
                  <KSpan fontWeight={600} text={`${albumGroup.album?.title || "-"}`} />
                </div>
                <div style={{ width: albumColumnWidths.ALBUM_UPC }} className="px-1">
                  <KSpan fontWeight={500} text={`${albumGroup.album?.upc || "-"}`} />
                </div>
                <div style={{ width: albumColumnWidths.ALBUM_DISTRIBUTOR }} className="px-1">
                  <KSpan fontWeight={500} text={`${albumGroup.album?.distributor || "-"}`} />
                </div>
                <div style={{ width: albumColumnWidths.TRACK_COUNT }} className="px-1">
                  <KSpan
                    fontWeight={500}
                    text={`${albumGroup.tracks.length} ${albumGroup.tracks.length === 1 ? "Track" : "Tracks"}`}
                  />
                </div>
                <div style={{ width: albumColumnWidths.OFFER_LTM }} className="px-1">
                  <KSpan fontWeight={600} text={`${formatNumber(albumGroup.ltmIncomeTotal, 2)} ${currency}`} />
                </div>
                <div style={{ width: albumColumnWidths.ADVANCE_OFFER }} className="px-1">
                  <KSpan fontWeight={600} text={`${formatNumber(albumGroup.advanceOfferTotal, 2)} ${currency}`} />
                </div>
              </button>
              {isExpanded && (
                <div className="mb-4 ml-6 rounded-[10px] border border-[#E5E7EB] bg-[#FAFAFA] px-4 py-3">
                  <div className="flex">
                    {Object.entries(trackColumnWidths).map(([header, width]) => {
                      return (
                        <div style={{ width }} key={`track_header_${albumGroup.key}_${header}`} className="px-1">
                          <KSpan
                            color="#111"
                            text={trackColumnLabels[header as keyof typeof trackColumnWidths]}
                            fontWeight={500}
                          />
                        </div>
                      )
                    })}
                  </div>
                  {albumGroup.tracks.map((takedownTrack: TakedownTrack) => {
                    const offer = takedownTrack.songSelection
                    const checkSplitRateWarnings = checkPotentialSplitRate(takedownTrack, artistId)
                    const trackBasedSplitRateWarnings = checkSplitRateWarnings.warnings
                    const isSingleAndMainArtist = checkSplitRateWarnings.isSingleAndMainArtist
                    const reportBasedSplitRateWarnings =
                      splitRateMissingRowNumber?.[takedownTrack?.isrc?.trim() || ""] || 0
                    const splitRateArray = splitRateMap?.[takedownTrack?.isrc?.trim() || ""] || []

                    const showAlert =
                      (trackBasedSplitRateWarnings.length > 0 ||
                        reportBasedSplitRateWarnings > 0 ||
                        !splitRateExistInSheet ||
                        splitRateArray.length > 1) &&
                      !offer.ownership &&
                      !isSingleAndMainArtist

                    return (
                      <div
                        key={`track_${takedownTrack.id}`}
                        className="mt-3 pt-3 flex gap-2"
                        style={{ borderTop: "1px dashed #D1D5DB", width: "100%" }}
                      >
                        <div style={{ width: trackColumnWidths.TRACK_TITLE }} className="px-1">
                          <KSpan fontWeight={500} text={`${takedownTrack.title || "-"}`} />
                        </div>
                        <div style={{ width: trackColumnWidths.TRACK_ISRC }} className="px-1">
                          <KSpan fontWeight={500} text={`${takedownTrack.isrc || "-"}`} />
                        </div>
                        <div
                          style={{
                            width: trackColumnWidths.SPLIT_RATE
                          }}
                          className="px-1"
                        >
                          <div
                            className="w-full flex flex-row items-center gap-1 pt-0.5 cursor-pointer"
                            onClick={() => {
                              setSelectedSong(offer)
                            }}
                          >
                            <KSpan fontWeight={500} text={`${offer.ownership?.toString() || "-"}`} />
                            {showAlert && (
                              <KTooltip
                                width="300px"
                                content={
                                  <div className="flex flex-col gap-0.5">
                                    {trackBasedSplitRateWarnings.length > 0 && (
                                      <div className="mb-1">
                                        <KSpan text="Potential Split Required" color="#111" fontWeight={600} />
                                      </div>
                                    )}
                                    {trackBasedSplitRateWarnings.length > 0 &&
                                      trackBasedSplitRateWarnings.map((warning, i) => {
                                        return <KSpan key={i} text={`• ${warning}`} color="#111" fontSize={13} />
                                      })}
                                    {(reportBasedSplitRateWarnings > 0 || !splitRateExistInSheet) && (
                                      <React.Fragment>
                                        <div className="my-1">
                                          <KSpan
                                            text="Potential Split Missing in Reports"
                                            color="#111"
                                            fontWeight={600}
                                          />
                                        </div>
                                        {!splitRateExistInSheet && (
                                          <KSpan
                                            text={`• There is no split rate column in the royalty report associated with the confirmed offer.`}
                                            color="#111"
                                            fontSize={13}
                                          />
                                        )}
                                        {reportBasedSplitRateWarnings > 0 && (
                                          <KSpan
                                            text={`• Split rate value is missing in ${reportBasedSplitRateWarnings} row(s) of the royalty report associated with the confirmed offer.`}
                                            color="#111"
                                            fontSize={13}
                                          />
                                        )}
                                      </React.Fragment>
                                    )}
                                    {splitRateArray.length > 1 && (
                                      <React.Fragment>
                                        <div className="my-1">
                                          <KSpan
                                            text="Split Rate Mismatch Detected in Reports"
                                            color="#111"
                                            fontWeight={600}
                                          />
                                        </div>

                                        <KSpan
                                          text={`• This track has inconsistent split rates across different rows of the royalty report associated with the confirmed offer.`}
                                          color="#111"
                                          fontSize={13}
                                        />
                                        <div className="ml-2 flex flex-col gap-0.5">
                                          {splitRateArray.map((splitRate, i) => {
                                            return <KSpan key={i} text={`→ ${splitRate}`} fontSize={11} color="#111" />
                                          })}
                                        </div>
                                      </React.Fragment>
                                    )}
                                  </div>
                                }
                              >
                                <div className="p-1">
                                  <img src="/admin_icons/alert-circle.svg" className="w-5 h-5 min-w-5 min-h-5" />
                                </div>
                              </KTooltip>
                            )}
                          </div>
                        </div>
                        <div style={{ width: trackColumnWidths.OFFER_LTM }} className="px-1">
                          <KSpan fontWeight={500} text={`${formatNumber(offer.ltmIncome || "0", 2)} ${currency}`} />
                        </div>
                        <div style={{ width: trackColumnWidths.ADVANCE_OFFER }} className="px-1">
                          <KSpan fontWeight={500} text={`${formatNumber(offer.advanceOffer || "0", 2)} ${currency}`} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
        <div className="mt-3 pt-3 flex" style={{ borderTop: "2px solid #111", width: "100%" }}>
          <div
            style={{
              width: `calc(${albumColumnWidths.TOGGLE} + ${albumColumnWidths.ALBUM_TITLE} + ${albumColumnWidths.ALBUM_UPC} + ${albumColumnWidths.ALBUM_DISTRIBUTOR} + ${albumColumnWidths.TRACK_COUNT} + ${albumColumnWidths.OFFER_LTM})`
            }}
            className="text-right pr-6"
          >
            <KSpan fontWeight={600} color="#111" text="Total" fontSize={14} />
          </div>
          <div style={{ width: albumColumnWidths.ADVANCE_OFFER }}>
            <KSpan
              fontWeight={600}
              color="#111"
              text={`${formatNumber(advanceOfferTotal, 2)} ${currency}`}
              fontSize={14}
            />
          </div>
        </div>
        {affiliateComissionExist && takedownBatch.confirmedOfferType === "advance" && (
          <div className="pt-1 flex">
            <div
              style={{
                width: `calc(${albumColumnWidths.TOGGLE} + ${albumColumnWidths.ALBUM_TITLE} + ${albumColumnWidths.ALBUM_UPC} + ${albumColumnWidths.ALBUM_DISTRIBUTOR} + ${albumColumnWidths.TRACK_COUNT} + ${albumColumnWidths.OFFER_LTM})`
              }}
              className="justify-end pr-6 flex flex-row items-center"
            >
              <KSpan fontWeight={600} text={`Scout Payout  (${affiliate?.affiliateDealTerms?.advanceCommissionRate}%)`} fontSize={14} />
            </div>
            <div style={{ width: albumColumnWidths.ADVANCE_OFFER }}>
              <KSpan
                fontWeight={600}
                text={`+${formatNumber((advanceOfferTotal * affiliateCommissionRate) / 100, 2)} ${currency}`}
                fontSize={14}
              />
            </div>
          </div>
        )}
        {affiliateComissionExist && takedownBatch.confirmedOfferType === "advance" && (
          <div className="pt-1 flex">
            <div
              style={{
                width: `calc(${albumColumnWidths.TOGGLE} + ${albumColumnWidths.ALBUM_TITLE} + ${albumColumnWidths.ALBUM_UPC} + ${albumColumnWidths.ALBUM_DISTRIBUTOR} + ${albumColumnWidths.TRACK_COUNT} + ${albumColumnWidths.OFFER_LTM})`
              }}
              className="justify-end pr-6 flex flex-row items-center"
            >
              <KSpan fontWeight={600} color="#111" text="Final Total" fontSize={16} />
            </div>
            <div className="border-t-2" style={{ width: albumColumnWidths.ADVANCE_OFFER }}>
              <KSpan
                fontWeight={600}
                color="#111"
                text={`${formatNumber(advanceOfferTotal + ((advanceOfferTotal * affiliateCommissionRate) / 100), 2)} ${currency}`}
                fontSize={16}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={selectedContractTakedown !== undefined} onClose={handleClose} className="relative z-250">
      <div className="fixed inset-0 bg-[#0000004d]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className="relative rounded-[10px] bg-[#fff] p-6 shadow-md"
          style={{ height: "90vh", overflowY: "scroll", minWidth: 1200, maxWidth: 1300 }}
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 cursor-pointer"
            aria-label="Close takedown modal"
          >
            <X size={22} color="#111" />
          </button>
          <div className="pr-8">
            <div className="flex items-center gap-4">
              <KTitleSpan text="Takedown Batch Based on Distributor and Revenue" fontSize={24} />
              <div className="items-center">
                <KButton
                text="Export"
                onClick={handleExport}
                disabled={!takedownBatch}
                background="#111"
                textColor="white"
                width={"80px"}
                height={"30px"}
                shadowDisabled
              />
              </div>
            </div>
            <KSpan
              text={`Selected User: ${selectedContractTakedown?.user?.firstName} ${selectedContractTakedown?.user?.lastName}`}
            />
            {loading && <Loading />}
            {renderTakedownBatches()}
          </div>
        </Dialog.Panel>
      </div>
      <SongSelectionOwnershipUpdateModal
        songSelection={selectedSong}
        setSongSelection={setSelectedSong}
        onSubmit={updateSongSelectionOwnership}
        loading={loading}
      />
    </Dialog>
  )
}

export default AdminTakedownContractModal
